import * as React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Spin } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { pageNavsStore } from '@src/shared/store/navs';
import { useFunnelAnalysis, updateFunnelAnalysisCache } from '@src/services/queries';
import { DashboardProvider } from '../store';
import Dashboard from '../Dashboard';

const { upsert: upsertNav, remove: removeNav } = pageNavsStore.getState();

const Saved = () => {
  // Id is confirmed by route param
  const id = useParams().id!;
  const { data, isFetching, isError, refetch } = useFunnelAnalysis([id]);
  // Simulated loading process, this is for the purpose of completely unmounting the component when id changes
  const { isLoading: isPending } = useQuery({
    queryKey: ['analysis', 'funnel', 'saved', 'pending', id],
    queryFn: () => null,
    cacheTime: 0,
    staleTime: 0,
  });
  const dashboardName = data?.config.name;

  const handleSave = React.useCallback(() => {
    void refetch();
  }, [refetch]);

  React.useEffect(() => {
    upsertNav({
      id,
      type: 'saved',
      name: dashboardName ?? 'loading...',
    });
  }, [id, dashboardName]);

  React.useEffect(() => {
    if (isError) {
      removeNav(id);
    }
  }, [isError, id]);

  // Each time when data is being fetched (re-save), force to unmount the Dashboard component to avoid showing stale data
  if (isFetching || isPending) {
    return (
      <Spin
        style={{
          height: '75vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      />
    );
  }

  if (isError || !data) return <Navigate replace to={'/'} />;

  return (
    <DashboardProvider data={data} onChange={updateFunnelAnalysisCache} onSave={handleSave}>
      <Dashboard />
    </DashboardProvider>
  );
};

export const SavedFunnelAnalysis = React.memo(Saved);
